import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  CarouselDots,
  ClippedPillButton,
  JazzLogoImg,
  LobbyBackground,
  LobbyContent,
  LobbyFooterBar,
  LogoImg,
  LogoRow,
  RegulatoryCode,
  RulesCard,
  StepDots,
  TeamLabelCorner,
  WelcomeHeading,
} from "../components/blueShell";
import {
  OptionBadge,
  PrimaryPillButton,
  QuestionCard,
  QuestionCardWrapper,
  QuestionHeaderRow,
  QuestionStepDots,
  QuestionTitleBlock,
  SvgSlideDeck,
  TimerBadge,
  TimerLabel,
  TimerSeconds,
} from "../components/quizChrome";
import { Spinner, TopBar } from "../components/shared";
import { questions } from "../game/questions";
import { OptionKey } from "../game/questions";
import { PlayerAnswer, PlayerRole } from "../game/types";
import { useCountdown } from "../game/useCountdown";
import { useGameSocket } from "../game/useGameSocket";

interface PlayerPageProps {
  role: PlayerRole;
}

const teamLabel: Record<PlayerRole, string> = {
  player1: "1",
  player2: "2",
};

function sameOptions(a: OptionKey[], b: OptionKey[]): boolean {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((key) => bSet.has(key));
}

const ConnectedText = styled.p`
  font-family: 'Madimi One', sans-serif;
  font-size: 1.75rem;
  text-transform: uppercase;
`;

const SECURITY_DATA_SLIDES = [
  ...Array.from({ length: 16 }, (_, i) => `DONNÉES DE SÉCURITÉ ${i + 1}.svg`),
  "INFORMATIONS PRODUIT.svg",
];
const LEGAL_SLIDES = ["ML.svg", "conditions-prescriptions.svg"];

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const LetterBox = styled.button<{ $backgroundColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 9.5rem;
  border-radius: 0.75rem;
  border: none;
  background-color: ${(props) => props.$backgroundColor};

  @media (max-width: 480px) {
    height: 5.5rem;
  }
`;

const LetterBoxStatic = styled.div<{ $backgroundColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 9.5rem;
  border-radius: 0.75rem;
  background-color: ${(props) => props.$backgroundColor};

  @media (max-width: 480px) {
    height: 5.5rem;
  }
`;

const LetterBadge = styled(OptionBadge)`
  width: 2.5rem;
  height: 2.5rem;
  font-size: 1rem;
`;

const BottomRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  margin-top: 1.25rem;
`;

const TeamLabelText = styled.span`
  position: absolute;
  left: 0;
  font-weight: bold;
  color: #1a237e;
  flex-shrink: 0;
`;

const ActionsText = styled.p`
  margin: 0;
`;

const ResultMessage = styled.p`
  font-weight: bold;
  margin: 0;
`;

const QuestionTopBar = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const SubText = styled.p`
  font-size: 0.8125rem;
  opacity: 0.85;
`;

function QuestionView({
  role,
  questionIndex,
  timerEndsAt,
  phase,
  onSubmit,
}: {
  role: PlayerRole;
  questionIndex: number;
  timerEndsAt: number | null;
  phase: "QUESTION" | "LOCKED" | "EXPLANATION";
  onSubmit: (answer: PlayerAnswer) => void;
}) {
  const question = questions[questionIndex];
  const [selected, setSelected] = useState<OptionKey[]>([]);
  const [submittedAnswer, setSubmittedAnswer] = useState<PlayerAnswer | null>(null);
  const remainingSeconds = useCountdown(phase === "QUESTION" ? timerEndsAt : null);
  const [displaySeconds, setDisplaySeconds] = useState(remainingSeconds);
  useEffect(() => {
    if (phase === "QUESTION") {
      setDisplaySeconds(remainingSeconds);
    }
  }, [phase, remainingSeconds]);

  useEffect(() => {
    setSelected([]);
    setSubmittedAnswer(null);
  }, [questionIndex]);

  const toggleOption = (key: OptionKey) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleSubmit = () => {
    const isCorrect = sameOptions(selected, question.correctAnswers);
    const answer: PlayerAnswer = { choices: selected, isCorrect };
    setSubmittedAnswer(answer);
    onSubmit(answer);
  };

  const revealed = phase === "EXPLANATION";

  return (
    <QuestionCardWrapper>
      <QuestionCard>
        <QuestionHeaderRow>
          <QuestionTitleBlock>
            <h2>{revealed ? "La solution" : "Sélectionner et valider les choix"}</h2>
            <QuestionStepDots>
              <span />
              <span />
              <span />
            </QuestionStepDots>
          </QuestionTitleBlock>
          {(phase === "QUESTION" || phase === "LOCKED") && (
            <TimerBadge>
              <TimerSeconds>{displaySeconds}</TimerSeconds>
              <TimerLabel>sec</TimerLabel>
            </TimerBadge>
          )}
        </QuestionHeaderRow>
        <OptionsGrid>
        {question.options.map((option) => {
          const isCorrect = question.correctAnswers.includes(option.key);
          const isSelected = selected.includes(option.key);

          let backgroundColor = "#1a237e";
          if (revealed) {
            const wasSelectedWrong = !isCorrect && submittedAnswer?.choices.includes(option.key);
            backgroundColor = isCorrect ? "#4caf50" : wasSelectedWrong ? "#b71c1c" : "#1a237e";
          } else if (isSelected) {
            backgroundColor = "#ef6c00";
          }

          const isSelectable = phase === "QUESTION" && submittedAnswer === null;

          if (!isSelectable) {
            return (
              <LetterBoxStatic key={option.key} $backgroundColor={backgroundColor}>
                <LetterBadge>{option.key}</LetterBadge>
              </LetterBoxStatic>
            );
          }

          return (
            <LetterBox
              key={option.key}
              $backgroundColor={backgroundColor}
              onClick={() => toggleOption(option.key)}
            >
              <LetterBadge>{option.key}</LetterBadge>
            </LetterBox>
          );
        })}
        </OptionsGrid>

        <BottomRow>
          <TeamLabelText>ÉQUIPE {teamLabel[role]}</TeamLabelText>
          {revealed ? (
            <ResultMessage>
              {submittedAnswer === null
                ? "Vous n'avez pas répondu à temps."
                : submittedAnswer.isCorrect
                  ? "Réponse correcte !"
                  : "Réponse incorrecte !"}
            </ResultMessage>
          ) : submittedAnswer !== null ? (
            <ActionsText>En attente de l'affichage des réponses</ActionsText>
          ) : phase === "LOCKED" ? (
            <ActionsText>Le temps est écoulé.</ActionsText>
          ) : (
            <PrimaryPillButton onClick={handleSubmit} disabled={selected.length === 0}>
              Valider son choix
            </PrimaryPillButton>
          )}
        </BottomRow>
      </QuestionCard>
    </QuestionCardWrapper>
  );
}

function WaitingForScoreView({ role }: { role: PlayerRole }) {
  return (
    <>
      <WelcomeHeading>
        La mise à jour du score de l'équipe va
        <br />
        s'afficher sur l'écran animateur.
      </WelcomeHeading>
      <StepDots>
        <span />
        <span />
        <span />
      </StepDots>
      <Spinner />
      <p>Tenez-vous prêt, la question suivante arrive...</p>
      <TeamLabelCorner>ÉQUIPE {teamLabel[role]}</TeamLabelCorner>
    </>
  );
}

function EndView({ role, scores }: { role: PlayerRole; scores: Record<PlayerRole, number> }) {
  const winnerMessage =
    scores.player1 === scores.player2
      ? "Égalité entre les deux équipes !"
      : `L'équipe ${scores.player1 > scores.player2 ? "1" : "2"} a accumulé le plus de bonnes réponses`;

  return (
    <>
      <WelcomeHeading>{winnerMessage}</WelcomeHeading>
      <LogoRow>
        <LogoImg src="/images/LOGO_EPI.png" alt="EPI & CO" />
      </LogoRow>
      <StepDots>
        <span />
        <span />
        <span />
      </StepDots>
      <p>
        <strong>Merci d'avoir participé au quiz.</strong>
      </p>
      <SubText>Les mentions légales sont affichées sur l'écran animateur.</SubText>
      <TeamLabelCorner>ÉQUIPE {teamLabel[role]}</TeamLabelCorner>
    </>
  );
}

export default function PlayerPage({ role }: PlayerPageProps) {
  const { state, socket } = useGameSocket(role);
  const [openPanel, setOpenPanel] = useState<"security" | "legal" | null>(null);

  useEffect(() => {
    const enterFullscreen = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen?.().catch(() => {});
      }
    };
    document.addEventListener("click", enterFullscreen);
    return () => document.removeEventListener("click", enterFullscreen);
  }, []);

  if (!state) {
    return <p>Connexion au serveur...</p>;
  }

  const clippedTopBar = (
    <TopBar>
      <ClippedPillButton
        $side="left"
        onClick={() => setOpenPanel(openPanel === "security" ? null : "security")}
      >
        {openPanel === "security" ? "Quitter les données de sécurité" : "Accès aux données de sécurité"}
      </ClippedPillButton>
      <ClippedPillButton $side="right" onClick={() => setOpenPanel(openPanel === "legal" ? null : "legal")}>
        {openPanel === "legal" ? "Quitter les mentions légales" : "Accès aux mentions légales"}
      </ClippedPillButton>
    </TopBar>
  );

  if (openPanel !== null) {
    const slides = openPanel === "security" ? SECURITY_DATA_SLIDES : LEGAL_SLIDES;
    return (
      <LobbyBackground>
        <LobbyContent>
          {clippedTopBar}
          <SvgSlideDeck slides={slides} />
        </LobbyContent>
        <LobbyFooterBar>
          <JazzLogoImg src="/images/logo_jazz_pharma_color.png" alt="Jazz Pharmaceuticals" />
          <RegulatoryCode>FR-EPX-2600006 - 26/04/61680130/PM/003</RegulatoryCode>
        </LobbyFooterBar>
      </LobbyBackground>
    );
  }

  if (state.phase === "LOBBY") {
    return (
      <LobbyBackground>
        <LobbyContent>
          {clippedTopBar}
          <WelcomeHeading>BIENVENUE SUR</WelcomeHeading>
          <LogoRow>
            <LogoImg src="/images/LOGO_EPI.png" alt="EPI & CO" />
          </LogoRow>
          <ConnectedText>
            Vous venez de vous
            <br />
            connecter dans l'équipe {teamLabel[role]}
          </ConnectedText>
          <CarouselDots>
            <span />
            <span />
            <span />
          </CarouselDots>
          <Spinner />
          <p>Tenez-vous prêt, le quiz va bientôt commencer...</p>
          <TeamLabelCorner>ÉQUIPE {teamLabel[role]}</TeamLabelCorner>
        </LobbyContent>
        <LobbyFooterBar>
          <JazzLogoImg src="/images/logo_jazz_pharma_color.png" alt="Jazz Pharmaceuticals" />
          <RegulatoryCode>FR-EPX-2600006 - 26/04/61680130/PM/003</RegulatoryCode>
        </LobbyFooterBar>
      </LobbyBackground>
    );
  }

  if (state.phase === "RULES") {
    return (
      <LobbyBackground>
        <LobbyContent>
          {clippedTopBar}
          <LogoRow>
            <LogoImg src="/images/LOGO_EPI.png" alt="EPI & CO" />
          </LogoRow>
          <RulesCard>
            <h2>Les règles du quiz</h2>
            <StepDots>
              <span />
              <span />
              <span />
            </StepDots>
            <p>Prêts à relever le défi en équipe ?</p>
            <p>
              L'objectif est simple : <strong>accumuler le plus de bonnes réponses.</strong>
            </p>
            <p>
              <strong>Attention au temps !</strong> Vous devrez vous concerter et voter avant que le
              chronomètre ne s'arrête.
            </p>
            <p>Bon quiz !</p>
          </RulesCard>
          <Spinner />
          <p>Tenez-vous prêt, l'animateur va partager la 1ère question ...</p>
          <TeamLabelCorner>ÉQUIPE {teamLabel[role]}</TeamLabelCorner>
        </LobbyContent>
        <LobbyFooterBar>
          <JazzLogoImg src="/images/logo_jazz_pharma_color.png" alt="Jazz Pharmaceuticals" />
          <RegulatoryCode>FR-EPX-2600006 - 26/04/61680130/PM/003</RegulatoryCode>
        </LobbyFooterBar>
      </LobbyBackground>
    );
  }

  if (
    state.phase === "QUESTION" ||
    state.phase === "LOCKED" ||
    (state.phase === "EXPLANATION" && !state.explanationSlidesStarted)
  ) {
    return (
      <LobbyBackground>
        <LobbyContent>
          <QuestionTopBar>
            <ClippedPillButton
              $side="left"
              onClick={() => setOpenPanel(openPanel === "security" ? null : "security")}
            >
              {openPanel === "security" ? "Quitter les données de sécurité" : "Accès aux données de sécurité"}
            </ClippedPillButton>
            <ClippedPillButton $side="right" onClick={() => setOpenPanel(openPanel === "legal" ? null : "legal")}>
              {openPanel === "legal" ? "Quitter les mentions légales" : "Accès aux mentions légales"}
            </ClippedPillButton>
          </QuestionTopBar>
          <QuestionView
            role={role}
            questionIndex={state.questionIndex}
            timerEndsAt={state.timerEndsAt}
            phase={state.phase}
            onSubmit={(answer) => socket?.emit("player:submitAnswer", answer)}
          />
        </LobbyContent>
        <LobbyFooterBar>
          <JazzLogoImg src="/images/logo_jazz_pharma_color.png" alt="Jazz Pharmaceuticals" />
          <RegulatoryCode>FR-EPX-2600006 - 26/04/61680130/PM/003</RegulatoryCode>
        </LobbyFooterBar>
      </LobbyBackground>
    );
  }

  if ((state.phase === "EXPLANATION" && state.explanationSlidesStarted) || state.phase === "SCORE") {
    return (
      <LobbyBackground>
        <LobbyContent>
          {clippedTopBar}
          <WaitingForScoreView role={role} />
        </LobbyContent>
        <LobbyFooterBar>
          <JazzLogoImg src="/images/logo_jazz_pharma_color.png" alt="Jazz Pharmaceuticals" />
          <RegulatoryCode>FR-EPX-2600006 - 26/04/61680130/PM/003</RegulatoryCode>
        </LobbyFooterBar>
      </LobbyBackground>
    );
  }

  return (
    <LobbyBackground>
      <LobbyContent>
        {clippedTopBar}
        <EndView role={role} scores={state.scores} />
      </LobbyContent>
      <LobbyFooterBar>
        <JazzLogoImg src="/images/logo_jazz_pharma_color.png" alt="Jazz Pharmaceuticals" />
        <RegulatoryCode>FR-EPX-2600006 - 26/04/61680130/PM/003</RegulatoryCode>
      </LobbyFooterBar>
    </LobbyBackground>
  );
}
